export interface PerformanceSummary {
  count: number
  p50: number
  p95: number
  max: number
  avg: number
}

const percentile = (values: number[], p: number): number => {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * p)))
  return sorted[idx]
}

export class PerformanceProbe {
  private starts = new Map<string, number>()
  private samples = new Map<string, number[]>()
  private readonly maxSamplesPerMetric: number

  constructor(maxSamplesPerMetric: number = 2000) {
    this.maxSamplesPerMetric = Math.max(1, maxSamplesPerMetric)
  }

  private pushSample(name: string, duration: number): void {
    const existing = this.samples.get(name) ?? []
    existing.push(duration)
    if (existing.length > this.maxSamplesPerMetric) {
      existing.splice(0, existing.length - this.maxSamplesPerMetric)
    }
    this.samples.set(name, existing)
  }

  markStart(name: string): void {
    this.starts.set(name, performance.now())
  }

  markEnd(name: string): number {
    const started = this.starts.get(name)
    if (started === undefined) return 0
    const duration = performance.now() - started
    this.pushSample(name, duration)
    this.starts.delete(name)
    return duration
  }

  record(name: string, durationMs: number): void {
    this.pushSample(name, durationMs)
  }

  getSummary(name: string): PerformanceSummary {
    const values = this.samples.get(name) ?? []
    if (values.length === 0) {
      return { count: 0, p50: 0, p95: 0, max: 0, avg: 0 }
    }
    const sum = values.reduce((acc, cur) => acc + cur, 0)
    return {
      count: values.length,
      p50: percentile(values, 0.5),
      p95: percentile(values, 0.95),
      max: Math.max(...values),
      avg: sum / values.length,
    }
  }

  getAllSummaries(): Record<string, PerformanceSummary> {
    const out: Record<string, PerformanceSummary> = {}
    for (const key of this.samples.keys()) {
      out[key] = this.getSummary(key)
    }
    return out
  }

  reset(): void {
    this.starts.clear()
    this.samples.clear()
  }
}

export interface ResourceSnapshot {
  activeOverlays: number
  createdGeometries: number
  disposedGeometries: number
  createdMaterials: number
  disposedMaterials: number
}

export class ResourceCounters {
  private state: ResourceSnapshot = {
    activeOverlays: 0,
    createdGeometries: 0,
    disposedGeometries: 0,
    createdMaterials: 0,
    disposedMaterials: 0,
  }

  addOverlay(count: number = 1): void {
    this.state.activeOverlays += count
  }

  clearOverlay(count: number = 1): void {
    this.state.activeOverlays = Math.max(0, this.state.activeOverlays - count)
  }

  geometryCreated(count: number = 1): void {
    this.state.createdGeometries += count
  }

  geometryDisposed(count: number = 1): void {
    this.state.disposedGeometries += count
  }

  materialCreated(count: number = 1): void {
    this.state.createdMaterials += count
  }

  materialDisposed(count: number = 1): void {
    this.state.disposedMaterials += count
  }

  snapshot(): ResourceSnapshot {
    return { ...this.state }
  }

  reset(): void {
    this.state = {
      activeOverlays: 0,
      createdGeometries: 0,
      disposedGeometries: 0,
      createdMaterials: 0,
      disposedMaterials: 0,
    }
  }
}

export class RenderScheduler {
  private dirtyReasons = new Set<string>()
  private rafId: number | null = null

  constructor(private readonly flushImpl: () => void) {}

  invalidate(reason: string): void {
    this.dirtyReasons.add(reason)
    if (this.rafId !== null) return

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null
      this.flush()
    })
  }

  flush(): void {
    if (this.dirtyReasons.size === 0) return
    this.dirtyReasons.clear()
    this.flushImpl()
  }

  dispose(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    this.dirtyReasons.clear()
  }
}
